import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Plus, Send, Search, Trash2, Edit3, X, Check } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { Skeleton } from '@/components/ui/skeleton.js';

interface TimeEntry {
  id: string;
  user_id: string;
  task_id: string;
  project_id: string;
  task_title?: string;
  project_name?: string;
  user_name?: string;
  duration_seconds: number;
  karachi_date: string;
  work_note?: string | null;
  review_state: string;
  is_manual: boolean;
  start_utc?: string;
  end_utc?: string;
}

export function TimeTrackingPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [memberFilter, setMemberFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortField, setSortField] = useState<string>('karachi_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Modals
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualData, setManualData] = useState({
    task_id: '',
    start_utc: '',
    end_utc: '',
    work_note: '',
  });

  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = useState({
    work_note: '',
    duration_hours: '',
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Delete Confirmation State
  const [deleteEntryTarget, setDeleteEntryTarget] = useState<{ id: string; name: string } | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!editingEntry && !showManualModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingEntry(null);
        setShowManualModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingEntry, showManualModal]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchEntries = async () => {
    try {
      const res = await api.get<{ data: TimeEntry[] }>('/api/v1/time-entries');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setEntries(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleManualSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/time-entries', manualData);
      setShowManualModal(false);
      setManualData({ task_id: '', start_utc: '', end_utc: '', work_note: '' });
      showToast('Manual entry created successfully!');
      fetchEntries();
    } catch (e: any) {
      alert(e.message || 'Failed to create manual entry');
    }
  };

  const handleTimesheetSubmit = async () => {
    const draftEntries = entries.filter((e) => e.review_state === 'draft');
    if (draftEntries.length === 0) {
      alert('No unsubmitted draft entries to submit.');
      return;
    }
    const today = new Date().toISOString().substring(0, 10);
    try {
      await api.post('/api/v1/timesheets/submit', {
        week_start: today,
        entry_ids: draftEntries.map((e) => e.id),
      });
      showToast('Timesheet submitted for review!');
      fetchEntries();
    } catch (e: any) {
      alert(e.message || 'Failed to submit timesheet');
    }
  };

  const handleOpenEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setEditForm({
      work_note: entry.work_note || '',
      duration_hours: (entry.duration_seconds / 3600).toFixed(2),
    });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    try {
      const updatedSeconds = Math.round(Number(editForm.duration_hours) * 3600);
      await api.patch(`/api/v1/time-entries/${editingEntry.id}`, {
        work_note: editForm.work_note,
        duration_seconds: updatedSeconds,
      });
      setEditingEntry(null);
      showToast('Time entry updated successfully!');
      fetchEntries();
    } catch (e: any) {
      alert(e.message || 'Failed to update time entry');
    }
  };

  const handleDeleteEntry = (id: string, name?: string) => {
    setDeleteEntryTarget({ id, name: name || 'this time entry' });
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntryTarget) return;
    const { id } = deleteEntryTarget;
    try {
      await api.delete(`/api/v1/time-entries/${id}`);
      setDeleteEntryTarget(null);
      showToast('Time entry deleted.');
      fetchEntries();
    } catch (e: any) {
      alert(e.message || 'Failed to delete time entry');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const uniqueMembers = Array.from(new Set(entries.map((e) => e.user_name).filter(Boolean)));
  const uniqueProjects = Array.from(new Set(entries.map((e) => e.project_name).filter(Boolean)));

  const filtered = entries.filter((e) => {
    const q = search.toLowerCase();
    const matchesSearch = q
      ? (e.task_title && e.task_title.toLowerCase().includes(q)) ||
        (e.project_name && e.project_name.toLowerCase().includes(q)) ||
        (e.work_note && e.work_note.toLowerCase().includes(q)) ||
        (e.user_name && e.user_name.toLowerCase().includes(q))
      : true;
    const matchesState = stateFilter ? e.review_state === stateFilter : true;
    const matchesMember = memberFilter ? (e.user_name === memberFilter || e.user_id === memberFilter) : true;
    const matchesProject = projectFilter ? (e.project_name === projectFilter || e.project_id === projectFilter) : true;
    const matchesDate = dateFilter ? e.karachi_date === dateFilter : true;
    return matchesSearch && matchesState && matchesMember && matchesProject && matchesDate;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (sortField === 'duration_seconds') {
      return sortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
    }
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Time Tracking</h1>
          <p className="helper-text mt-1">Precision work intervals, weekly review workflow, and Karachi timezone boundaries.</p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => setShowManualModal(true)}
            className="flex items-center space-x-1 text-xs"
          >
            <Plus className="h-4 w-4" />
            <span>Manual Entry</span>
          </Button>
          <Button
            onClick={handleTimesheetSubmit}
            className="bg-brand-teal hover:bg-brand-teal-action text-white flex items-center space-x-1 text-xs"
          >
            <Send className="h-4 w-4" />
            <span>Submit Timesheet</span>
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 bg-white p-3 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto">
          <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search time entries..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
          >
            <option value="">All Review States</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={projectFilter}
            onChange={(e) => {
              setProjectFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
          >
            <option value="">All Projects</option>
            {uniqueProjects.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={memberFilter}
            onChange={(e) => {
              setMemberFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
          >
            <option value="">All Members</option>
            {uniqueMembers.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div className="flex items-center space-x-1">
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
            />
            {dateFilter && (
              <button
                type="button"
                onClick={() => {
                  setDateFilter('');
                  setCurrentPage(1);
                }}
                className="text-[11px] text-brand-teal hover:underline px-1 font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort:</span>
          <SortButton label="Date" field="karachi_date" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Duration" field="duration_seconds" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Task" field="task_title" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Project" field="project_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {/* Entries Table */}
      <Card>
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800">Recorded Intervals</CardTitle>
          <Badge variant="secondary">{sorted.length} entries</Badge>
        </CardHeader>
        <div className="divide-y divide-slate-100 overflow-x-auto">
          {loading ? (
            <div className="p-4 space-y-3.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3.5 w-24 rounded-full" />
                      <Skeleton className="h-3.5 w-14 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-72" />
                  </div>
                  <div className="flex items-center space-x-4">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No time entries found matching criteria. Use Start Timer on any task or log a manual entry!
            </div>
          ) : (
            paginated.map((entry) => (
              <div key={entry.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs hover:bg-slate-50/70 transition-colors">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-slate-800">{entry.task_title || 'General Task'}</span>
                    <span className="text-slate-400">•</span>
                    <span className="text-brand-teal font-medium">{entry.project_name || 'Project'}</span>
                    <Badge variant={entry.review_state as any} className="text-[10px] capitalize">
                      {entry.review_state.replace('_', ' ')}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {entry.is_manual ? 'Manual' : 'Timer'}
                    </Badge>
                  </div>
                  {entry.work_note && <p className="text-slate-600">{entry.work_note}</p>}
                </div>
                <div className="flex items-center space-x-4 text-slate-500 font-mono">
                  <span>{entry.karachi_date}</span>
                  <span className="font-bold text-slate-800 text-sm">
                    {(entry.duration_seconds / 3600).toFixed(2)} hrs
                  </span>
                  <div className="flex items-center space-x-1 pl-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleOpenEdit(entry)}
                      className="h-7 w-7 text-slate-400 hover:text-brand-navy"
                      title="Edit Entry"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    {isOwner && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteEntry(entry.id, `${entry.user_name || 'Team Member'} (${(entry.duration_seconds / 3600).toFixed(2)} hrs on ${entry.karachi_date})`)}
                        className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50"
                        title="Delete Entry"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <Pagination
          totalItems={sorted.length}
          currentPage={validCurrentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </Card>

      {/* Manual Entry Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowManualModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Log Manual Work Interval</h2>
              <button onClick={() => setShowManualModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleManualSubmit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Task ID *</label>
                <Input
                  required
                  value={manualData.task_id}
                  onChange={(e) => setManualData({ ...manualData, task_id: e.target.value })}
                  placeholder="Task ID"
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Start Time *</label>
                  <Input
                    required
                    type="datetime-local"
                    value={manualData.start_utc}
                    onChange={(e) => setManualData({ ...manualData, start_utc: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">End Time *</label>
                  <Input
                    required
                    type="datetime-local"
                    value={manualData.end_utc}
                    onChange={(e) => setManualData({ ...manualData, end_utc: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Work Note *</label>
                <Input
                  required
                  value={manualData.work_note}
                  onChange={(e) => setManualData({ ...manualData, work_note: e.target.value })}
                  placeholder="Reason / Summary of completed work"
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setShowManualModal(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Entry
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setEditingEntry(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Edit Time Entry</h2>
              <button onClick={() => setEditingEntry(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Duration (Hours) *</label>
                <Input
                  required
                  type="number"
                  step="0.05"
                  value={editForm.duration_hours}
                  onChange={(e) => setEditForm({ ...editForm, duration_hours: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Work Note</label>
                <Input
                  value={editForm.work_note}
                  onChange={(e) => setEditForm({ ...editForm, work_note: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setEditingEntry(null)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!deleteEntryTarget}
        itemType="Time Entry"
        itemName={deleteEntryTarget?.name}
        onClose={() => setDeleteEntryTarget(null)}
        onConfirm={confirmDeleteEntry}
      />
    </div>
  );
}
